import React from "react";
import { fmtNum, fmtDate } from "@finance/lib/api";
import { Checkbox } from "@finance/components/ui/checkbox";
import { LockSimple, GlobeSimple, PencilSimple, Trash } from "@phosphor-icons/react";
import { catColor, tagClass, PRIORITY_COLORS } from "@finance/lib/actionColors";

const today = () => new Date().toISOString().slice(0, 10);

export function CategoryBadge({ category, className = "", noDot = false }) {
  const c = catColor(category);
  return (
    <span data-testid={`cat-badge-${category}`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${c.badge} ${className}`}>
      {!noDot && <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />}{category}
    </span>
  );
}

export function PriorityBadge({ priority }) {
  if (!priority) return null;
  return <span data-testid={`priority-badge-${priority}`} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${PRIORITY_COLORS[priority] || ""}`}>{priority}</span>;
}

export function TagChips({ tags, onTagClick }) {
  if (!tags || tags.length === 0) return null;
  return (
    <>
      {tags.map((t, i) => onTagClick ? (
        <button key={i} data-testid={`action-tag-${t}`} onClick={(e) => { e.stopPropagation(); onTagClick(t); }} className={`inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[11px] font-medium transition-all hover:ring-1 hover:ring-offset-1 ${tagClass(t)}`}>#{t}</button>
      ) : (
        <span key={i} data-testid={`action-tag-${t}`} className={`inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[11px] font-medium ${tagClass(t)}`}>#{t}</span>
      ))}
    </>
  );
}

// Rich card used by the Grid & Board views. onToggle/onEdit/onDelete/onTagClick are optional.
export function ActionCard({ a, onToggle, onEdit, onDelete, onTagClick, showCreatedBy, compact }) {
  const c = catColor(a.category);
  const done = a.status === "Completed";
  const overdue = !done && a.due_date && a.due_date < today();
  return (
    <div data-testid={`action-card-${a.id}`} className={`soft-card soft-card--hover lx-row-enter group relative flex flex-col ${compact ? "p-4" : "p-5"} ${done ? "opacity-60 hover:opacity-100" : ""}`}>
      <div className="flex items-start gap-2.5">
        {onToggle && (
          <Checkbox checked={done} data-testid={`action-toggle-${a.id}`} onCheckedChange={() => onToggle(a)} className="mt-0.5" />
        )}
        <div className={`flex-1 break-words text-sm leading-snug ${compact ? "line-clamp-3" : ""} ${done ? "text-gray-400 line-through" : "text-gray-900 dark:text-gray-100"}`}>{a.details}</div>
        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          {onEdit && <button data-testid={`action-edit-${a.id}`} onClick={() => onEdit(a)} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"><PencilSimple size={15} /></button>}
          {onDelete && <button data-testid={`action-delete-btn-${a.id}`} onClick={() => onDelete(a)} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"><Trash size={15} /></button>}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-[11px] text-gray-500">
        <span className="inline-flex items-center gap-1.5">
          <span data-testid={`cat-dot-${a.category}`} className={`h-2 w-2 rounded-full ${done ? "bg-gray-300" : overdue ? "bg-red-500" : c.dot}`} />
          <span className="font-medium text-gray-600 dark:text-gray-300">{a.category}</span>
        </span>
        {a.priority && a.priority !== "Medium" && <><span className="text-gray-300">·</span><span className={`font-semibold ${a.priority === "High" ? "text-red-600" : "text-gray-400"}`}>{a.priority}</span></>}
        {overdue && <><span className="text-gray-300">·</span><span data-testid={`action-overdue-${a.id}`} className="font-semibold uppercase tracking-wide text-red-600">Overdue</span></>}
        {(a.tags || []).length > 0 && <><span className="text-gray-300">·</span><TagChips tags={a.tags} onTagClick={onTagClick} /></>}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-gray-400">
        <span className="inline-flex items-center gap-1">
          <span className="grid h-4 w-4 place-items-center rounded-full bg-gray-200 text-[9px] font-bold text-gray-600">{(a.assigned_to_name || "?").slice(0, 1).toUpperCase()}</span>
          {a.assigned_to_name}
        </span>
        {a.amount != null && <><span className="text-gray-300">·</span><span className="lx-num font-medium text-gray-500">{fmtNum(a.amount)}</span></>}
        {a.due_date && (
          <>
            <span className="text-gray-300">·</span>
            <span data-testid={`action-due-${a.id}`} className={`lx-num ${overdue ? "font-semibold text-red-600" : done ? "text-gray-400" : "text-gray-500"}`}>Due {fmtDate(a.due_date)}</span>
          </>
        )}
        <span className="text-gray-300">·</span>
        {a.visibility === "Private" ? (
          <span className="inline-flex items-center gap-1"><LockSimple size={12} />Private</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[#1B4383]"><GlobeSimple size={12} />Public</span>
        )}
        {showCreatedBy && a.created_by_name && <><span className="text-gray-300">·</span><span>by {a.created_by_name}</span></>}
      </div>

      {a.notes && !compact && <div className="mt-3 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-500 dark:bg-white/5">{a.notes}</div>}
    </div>
  );
}
